import React, { useState } from 'react';
import { useT } from '@/i18n/useT';
import { useSimulationStore } from '@/store/simulationStore';
import type { ModbusServerDevice } from '@/simulation/process/devices/modbus';
import type { ModbusDataArea } from '@/simulation/modbus/ModbusTypes';
import styles from './ModbusMonitorPanel.module.css';

export const ModbusMonitorPanel: React.FC = () => {
  const t = useT();
  const engine = useSimulationStore((s) => s.engine);

  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [area, setArea] = useState<ModbusDataArea>('HOLDING_REGISTER');
  const [startAddress, setStartAddress] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(10);

  // Derive Modbus Server Devices
  const servers = Array.from(engine.process.devices.values()).filter(d => d.type === 'modbus-server') as ModbusServerDevice[];

  if (servers.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>{t('bottom.modbus.noServers')}</p>
      </div>
    );
  }

  // Auto-select first server if none selected
  const activeServerId = selectedServerId || servers[0]?.id || '';
  const activeServer = servers.find(s => s.id === activeServerId);

  // Build the table rows for the requested area
  const rows = [];
  for (let i = 0; i < quantity; i++) {
    const addr = startAddress + i;
    let valStr = '---';
    let mappingStr = 'None';
    
    if (activeServer) {
      const mapping = activeServer.mapper.getMappings().find(m => m.area === area && m.modbusAddress === addr);
      if (mapping) {
        mappingStr = mapping.plcAddress;
        try {
          const val = activeServer.mapper.read(engine.host, area, addr);
          if (val !== null) {
            valStr = val.toString();
          }
        } catch {
          valStr = 'ERR';
        }
      }
    }
    
    rows.push({
      addr,
      mappingStr,
      valStr,
      isReadOnly: area === 'DISCRETE_INPUT' || area === 'INPUT_REGISTER',
      area
    });
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <label>
          Server:
          <select value={activeServerId} onChange={(e) => setSelectedServerId(e.target.value)}>
            {servers.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
          </select>
        </label>
        
        <label>
          Data Area:
          <select value={area} onChange={(e) => setArea(e.target.value as ModbusDataArea)}>
            <option value="COIL">Coils (0xxxx)</option>
            <option value="DISCRETE_INPUT">Discrete Inputs (1xxxx)</option>
            <option value="INPUT_REGISTER">Input Registers (3xxxx)</option>
            <option value="HOLDING_REGISTER">Holding Registers (4xxxx)</option>
          </select>
        </label>
        
        <label>
          Start Address:
          <input type="number" min={0} max={65535} value={startAddress} onChange={(e) => setStartAddress(Number(e.target.value))} />
        </label>
        
        <label>
          Quantity:
          <input type="number" min={1} max={100} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} />
        </label>
      </div>
      
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Address</th>
              <th>Mapping</th>
              <th>Value</th>
              <th>Access</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.addr}>
                <td>{r.addr}</td>
                <td>{r.mappingStr}</td>
                <td className={styles.valueCell}>{r.valStr}</td>
                <td>{r.isReadOnly ? 'R/O' : 'R/W'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
